'use client';

import Image from 'next/image';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface ProductGalleryProps {
  images: { src: string; alt: string }[];
  /** Nombre del producto para a11y */
  productName: string;
  /** Fallback src cuando una imagen falla (típicamente placeholder de la colección). */
  fallbackSrc?: string;
}

/**
 * Galería PDP — patrón zarahome.com (docs/patterns-zh-observed.md §5):
 * desktop stack vertical; mobile carousel swipe horizontal con dots.
 * Ratio 3:4 desktop / 4:5 mobile.
 *
 * Si una imagen Cloudinary 404'ea (publicId stale), cae automáticamente
 * a `fallbackSrc` (placeholder local de la colección) gracias al onError
 * handler — evita huecos visuales en producción.
 */
export function ProductGallery({ images, productName, fallbackSrc }: ProductGalleryProps) {
  const [active, setActive] = useState(0);
  // Map de imágenes que ya fallaron para usar fallback
  const [failed, setFailed] = useState<Set<number>>(new Set());

  if (images.length === 0) return null;

  function resolveSrc(idx: number): string {
    if (failed.has(idx) && fallbackSrc) return fallbackSrc;
    return images[idx]!.src;
  }

  function markFailed(idx: number) {
    setFailed((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  }

  return (
    <div className="relative">
      {/* Desktop: stack vertical */}
      <div className="hidden flex-col gap-1 md:flex">
        {images.map((img, i) => (
          <div
            key={i}
            className="relative aspect-[3/4] w-full overflow-hidden bg-[var(--color-surface)]"
          >
            <Image
              src={resolveSrc(i)}
              alt={img.alt || `${productName} · vista ${i + 1}`}
              fill
              sizes="(min-width: 1280px) 55vw, 45vw"
              priority={i === 0}
              className="object-contain"
              onError={() => markFailed(i)}
            />
          </div>
        ))}
      </div>

      {/* Mobile: carrusel horizontal con dots */}
      <div className="relative md:hidden">
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-[var(--color-surface)]">
          <Image
            src={resolveSrc(active)}
            alt={images[active]!.alt || `${productName} · vista ${active + 1}`}
            fill
            sizes="100vw"
            priority
            className="object-contain"
            onError={() => markFailed(active)}
          />
        </div>
        {images.length > 1 ? (
          <div className="mt-4 flex items-center justify-center gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Ver imagen ${i + 1}`}
                className={cn(
                  'h-1.5 w-6 rounded-full transition-colors',
                  i === active ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border-strong)]'
                )}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
